<template>
  <div
    ref="tooltipRef"
    class="tooltip"
    :class="
      'tooltip__' +
      arrowPosition.split('-')[0] +
      ' tooltip__' +
      arrowPosition +
      ' tooltip__theme-' +
      theme
    "
    :style="{ position: positionAbsoulute ? 'absolute' : 'fixed' }"
  >
    {{ text }}
    <TooltipArrow class="tooltip__arrow" />
  </div>
</template>

<script lang="ts" setup>
//  DISCLAIMER! The parent of the tooltip need to have position: relative

import TooltipArrow from '@/design-system/atoms/TooltipArrow.vue'
import { computed, ref, watch } from 'vue'

/*
Valid arrowPosition props:
----------------------------------------
bottom-left, bottom-center, bottom-right
top-left, top-center, top-right
center-left, center-right
----------------------------------------
*/
const props = defineProps({
  arrowPosition: { type: String, default: 'top-center' },
  pointerEvent: { type: Object, default: () => {} },
  text: { type: String, default: '' },
  theme: { type: String, default: 'dark' }, // dark (default) or light
  positionAbsoulute: { type: Boolean, default: false }
})
const pointerPosition = computed(() => {
  return props.pointerEvent
})
const tooltipRef = ref<HTMLElement | null>(null)
watch(pointerPosition, (newPos) => {
  const tooltip = tooltipRef.value
  if (!tooltip) return

  tooltip.style.left = newPos.x + 'px'
  tooltip.style.top = newPos.y + 'px'
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/variables.scss';
.tooltip {
  border-radius: variables.$border-radius-small;
  display: inline-block;
  filter: drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.16));
  padding: variables.$padding-large variables.$padding-xlarge;
  position: fixed;
  max-width: variables.$tooltip-width;
  min-width: variables.$tooltip-min-width;
  white-space: pre-line;
  width: max-content;
  word-wrap: break-word;
  z-index: variables.$z-index-tooltip;
  @include font-mixins.desktop-regular-label;
  &__arrow {
    position: absolute;
  }
  &__bottom,
  &__top {
    &-left {
      left: calc(50% - #{variables.$padding-xlarge + variables.$margin-medium-large});
      .tooltip__arrow {
        left: variables.$position-xlarge;
      }
    }
    &-center {
      left: 50%;
      translate: -50%;
      .tooltip__arrow {
        left: 50%;
        margin-left: -(variables.$margin-medium-large);
      }
    }
    &-right {
      right: calc(50% - #{variables.$padding-xlarge + variables.$margin-medium-large});
      .tooltip__arrow {
        right: variables.$position-xlarge;
      }
    }
  }
  &__bottom {
    bottom: 100%;
    margin-bottom: variables.$margin-xlarge;
    .tooltip__arrow {
      top: 100%;
    }
  }
  &__top {
    margin-top: variables.$margin-small;
    top: 100%;
    .tooltip__arrow {
      bottom: 100%;
      transform: rotate(180deg);
    }
  }
  &__center {
    margin-top: variables.$margin-medium;
    translate: 0 -50%;
    .tooltip__arrow {
      top: 50%;
      margin: calc(-#{variables.$margin-medium-large} / 2);
    }
    &-left {
      margin-left: variables.$margin-xlarge;
      .tooltip__arrow {
        right: 100%;
        transform: rotate(90deg);
      }
    }
    &-right {
      margin-right: variables.$margin-xlarge;
      right: 100%;
      .tooltip__arrow {
        left: 100%;
        transform: rotate(270deg);
      }
    }
  }

  &__theme-dark {
    background-color: variables.$color-greyscale-content;
    color: variables.$color-pure-white;
    .tooltip__arrow {
      color: variables.$color-greyscale-content;
    }
  }
  &__theme-light {
    background-color: variables.$color-greyscale-background;
    color: variables.$color-greyscale-content;
    .tooltip__arrow {
      color: variables.$color-greyscale-background;
    }
  }
}
</style>
